import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    this.#imageSizeObserver = new ResizeObserver((entries) => {
      for (const { target } of entries) this.#updateCardImageSizes(target);
    });
    this.#cardMutationObserver = new MutationObserver(this.#syncCardImageSizes);
    this.#cardMutationObserver.observe(this, { childList: true, subtree: true });
    this.#syncCardImageSizes();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
  }

  #imageSizeObserver;
  #cardMutationObserver;
  #observedGalleries = new Set();

  #updateCardImageSizes = (gallery) => {
    const width = Math.ceil(gallery.getBoundingClientRect().width);
    if (width <= 0) return;
    for (const image of gallery.querySelectorAll('img.product-media__image')) {
      const sizes = `${image.loading === 'lazy' ? 'auto, ' : ''}${width}px`;
      if (image.sizes !== sizes) image.sizes = sizes;
    }
  };

  #syncCardImageSizes = () => {
    const galleries = new Set(this.querySelectorAll('[data-responsive-collection-card]'));
    for (const gallery of this.#observedGalleries) {
      if (galleries.has(gallery)) continue;
      this.#imageSizeObserver.unobserve(gallery);
      this.#observedGalleries.delete(gallery);
    }
    for (const gallery of galleries) {
      if (!this.#observedGalleries.has(gallery)) {
        this.#imageSizeObserver.observe(gallery);
        this.#observedGalleries.add(gallery);
      }
      this.#updateCardImageSizes(gallery);
    }
  };

  disconnectedCallback() {
    this.#imageSizeObserver?.disconnect();
    this.#cardMutationObserver?.disconnect();
    this.#observedGalleries.clear();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
